import {Record, Map, List, type RecordOf} from 'immutable';
import {
  RangeSelectionRecord,
  type ActiveUploadRecordType,
  type RangeSelectionRecordType,
  type SelectionRecordType,
} from '@packages/shared/records';
import type {
  LinkToolbarChanges,
  RichTextComponentSource,
} from '@packages/shared/types/ContentEditor';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {CollectionItem} from '@packages/systems/cms/types';
import type {FieldType} from '@packages/systems/dynamo/records/Field';
import {AI_CONTENT_GENERATION_ENTRY} from '@packages/systems/ai/writingAssistant';
import {NumberConstraintsType} from '@packages/systems/core/utils/NumberUtils';
import {wfdl} from '@packages/systems/wfdl-parser';
import {Expressions} from '@packages/systems/wfdl';

// Component prop type for ContentEditor
export type ComponentPropType = {
  type: 'Number' | 'PlainText' | 'RichText';
  constraints?: NumberConstraintsType;
} | null;

type PendingFormatToggleShape = {
  bold: boolean;
  italic: boolean;
};

type ContentEditorUndoStackItem = {
  subtree: ExpressionElement;
  selection: SelectionRecordType;
  action: any;
};

export type ContentEditorUndoStackItemRecordType =
  RecordOf<ContentEditorUndoStackItem>;

const _ContentEditorUndoStackItemRecord = Record({
  subtree: null,
  selection: null,
  action: null,
});

export const ContentEditorUndoStackItemRecord: (
  item: ContentEditorUndoStackItem
) => ContentEditorUndoStackItemRecordType =
  _ContentEditorUndoStackItemRecord as any;

export type PendingFormatToggleType = RecordOf<PendingFormatToggleShape>;

const pendingFormatToggleDefaults = {bold: false, italic: false};

export const PendingFormatToggle: Record.Factory<PendingFormatToggleShape> =
  Record(pendingFormatToggleDefaults);

type UndoRedoCache = {
  [item: string]: {
    [fieldSlug: string]: {
      undoStack: List<ContentEditorUndoStackItemRecordType>;
      redoStack: List<ContentEditorUndoStackItemRecordType>;
    };
  };
};

type SlashCommand = {
  selection: RangeSelectionRecordType | null;
  filter: string | null;
  isMenuFocused: boolean;
};

// AiData is a one-time use and will be replaced with each new Content Editor AI request
type AiData = {
  // Content generation is in progress
  isLoading: boolean;
  // Stores the 3 options generated by AI for the user to choose from
  generatedOptions: Array<string> | Array<ExpressionElement[]>;
  // The index for the option in generatedOptions currently being previewed to the user
  currentOptionIndex: number;
  // Stores the user-entered AI prompt
  userPrompt: string;
  // Whether to show the quick modification prompt menu
  showQuickModMenu: boolean;
  // Stores the initial selection when the AI toolbar is opened. Ths is used to keep the
  // toolbar from moving when the generated options are changed
  selectionForAiInsertion: RangeSelectionRecordType | null;
  // subtree with a hole in it where the AI-generated content will be inserted.
  subtreeForAiInsertion: ExpressionElement | null;
  // Whether generated AI content has been staged for a CMS-bound save.
  pendingCmsAiAttribution: boolean;
  // Is the AI feedback modal open
  isFeedbackModalOpen: boolean;
  // used to trigger request (e.g. when subtree adds a new element and we want to request content to fill it. see ContentEditorAiRequestEngine)
  isPendingRequestStartFromLlm: boolean;
  // format of response from LLM - either plain text or html.
  outputFormat: 'PlainText' | 'RichText' | null;
  // html of the subtree marking the users selection within the html
  htmlSelectionContext: string | null;
  // Stores the selection when the AI toolbar is opened. This is used to keep the toolbar from moving when the generated options are changed
  firstGeneratedOptionSelectionRect:
    | RangeSelectionRecordType['rangeClientRect']
    | null;
  // The entry point for the AI generation request
  generationEntry?: AI_CONTENT_GENERATION_ENTRY;
};

export type ContentEditorInactiveShape = {
  subtree: null;
  selection: null;
  allowNativeRender: boolean;
  consecutiveSpacebarAttempts: 0;
  formatToggle: RecordOf<PendingFormatToggleShape>;
  activeToolbar: null;
  // nestedInAnchor is true, when the subtree's parent or any other ancestor
  // is an <a> node.
  nestedInAnchor: false;
  activeUploads: Map<any, any>;
  undoStack: List<ContentEditorUndoStackItemRecordType>;
  redoStack: List<ContentEditorUndoStackItemRecordType>;
  breakOutSpanLinks: false;
  breakOutElement: null;
  pendingLinkToolbarChanges: null;
  // When editing Dyn-bound content in the canvas
  collectionId: null;
  itemId: null;
  fromModal: false;
  editingContentEditorEmbed: false;
  editingContentEditorCodeBlock: false;
  editingNodeSettings: false;
  item: null;
  field: null;
  hasServerError: false;
  validationErrorShown: false;
  confirmDiscardIsOpen: false;
  undoRedoCache: UndoRedoCache;
  itemFieldIsSaving: false;
  slashCommand: SlashCommand;
  aiData: AiData;
  componentProp: ComponentPropType;
  shouldSyncToComponentMap: boolean;
  isAutoSaveDisabled: boolean;
  removedComponentIds: string[];
  addedComponentIds: string[];
  componentChangeSource: RichTextComponentSource | null;
  shouldCloseAfterComponentAdd: boolean;
  isOnPrimaryLocale: boolean;
  isPageBranch: boolean;
  isDataManagerEditing: false;
  localizationKey: null;
};

export type ContentEditorInactive = RecordOf<ContentEditorInactiveShape>;

const contentEditorDefaults: ContentEditorInactiveShape = {
  subtree: null,
  selection: null,
  allowNativeRender: false,
  consecutiveSpacebarAttempts: 0,
  formatToggle: PendingFormatToggle(),
  activeToolbar: null,
  // nestedInAnchor is true, when the subtree's parent or any other ancestor
  // is an <a> node.
  nestedInAnchor: false,
  activeUploads: Map(),
  undoStack: List(),
  redoStack: List(),
  breakOutSpanLinks: false,
  breakOutElement: null,
  pendingLinkToolbarChanges: null,
  // When editing Dyn-bound content in the canvas
  collectionId: null,
  itemId: null,
  item: null,
  field: null,
  fromModal: false,
  editingContentEditorEmbed: false,
  editingContentEditorCodeBlock: false,
  editingNodeSettings: false,
  hasServerError: false,
  validationErrorShown: false,
  confirmDiscardIsOpen: false,
  undoRedoCache: {},
  itemFieldIsSaving: false,
  slashCommand: {
    selection: null,
    filter: null,
    isMenuFocused: false,
  },
  aiData: {
    isLoading: false,
    generatedOptions: [],
    currentOptionIndex: 0,
    userPrompt: '',
    showQuickModMenu: false,
    selectionForAiInsertion: null,
    subtreeForAiInsertion: null,
    pendingCmsAiAttribution: false,
    isFeedbackModalOpen: false,
    isPendingRequestStartFromLlm: false,
    outputFormat: null,
    htmlSelectionContext: null,
    firstGeneratedOptionSelectionRect: null,
    generationEntry: null,
  },
  componentProp: null,
  shouldSyncToComponentMap: false,
  isAutoSaveDisabled: false,
  removedComponentIds: [],
  addedComponentIds: [],
  componentChangeSource: null,
  shouldCloseAfterComponentAdd: false,
  isOnPrimaryLocale: true,
  isPageBranch: false,
  isDataManagerEditing: false,
  localizationKey: null,

  // // TODO (Yoav): copied from RichText.jsx, consider changing the shape of some of this to make more sense.
  // $figureElement: null,
  // figureType: 'image',
  // allowLink: false,
  // persistChanges: true,
  // figureTitle: '',
  // figureUrl: '',
  // figureSrc: '',
  // figureAlt: '',
  // figureLink: '',
  // figureLinkType: '',
  // figureThumbUrl: '',
  // figureDuration: 0,
  // figureSrcWidth: 0,
  // figureSrcHeight: 0,
  // figureMaxWidth: 0,
  // figureMaxHeight: 0,
  // figureFileSize: 0,
  // figureAspectRatio: 0,
  // figureDimensions: {width: 0, height: 0},
  // $linkElement: null,
  // linkMode: 'anchor',
  // href: '',
  // sections: [],
  // pages: {},
};

// Note: Many utils/helpers are/can only executed when something is being edited, certain values will never be null/undefined
// while editing, so we can use this type in those cases. This saves us having to pepper conditionals for values which if null
// in those places indicate a serious issue that should fail hard.
export type ContentEditorActiveShape = {
  subtree: ExpressionElement;
  selection: SelectionRecordType;
  allowNativeRender: boolean;
  consecutiveSpacebarAttempts: number;
  activeToolbar: string | null;
  formatToggle: RecordOf<PendingFormatToggleShape>;
  nestedInAnchor: boolean;
  activeUploads: Map<string, ActiveUploadRecordType>;
  undoStack: List<ContentEditorUndoStackItemRecordType>;
  redoStack: List<ContentEditorUndoStackItemRecordType>;
  breakOutSpanLinks: boolean;
  breakOutElement: ExpressionElement | null;
  pendingLinkToolbarChanges: LinkToolbarChanges | null | undefined;
  collectionId: string | null;
  itemId: string | null;
  item: CollectionItem | null;
  field: FieldType | null;
  fromModal: boolean;
  editingContentEditorEmbed: boolean;
  editingContentEditorCodeBlock: boolean;
  editingNodeSettings: boolean;
  hasServerError: boolean;
  validationErrorShown: boolean;
  confirmDiscardIsOpen: boolean;
  undoRedoCache: UndoRedoCache;
  itemFieldIsSaving: boolean;
  slashCommand: SlashCommand;
  aiData: AiData;
  componentProp: ComponentPropType;
  shouldSyncToComponentMap: boolean;
  isAutoSaveDisabled: boolean;
  removedComponentIds: string[];
  addedComponentIds: string[];
  componentChangeSource: RichTextComponentSource | null;
  shouldCloseAfterComponentAdd: boolean;
  isOnPrimaryLocale: boolean;
  isPageBranch: boolean;
  isDataManagerEditing: boolean;
  localizationKey: string | null;
};

export type ContentEditorActive = RecordOf<ContentEditorActiveShape>;

export const ContentEditorState: Record.Factory<ContentEditorInactiveShape> =
  Record(contentEditorDefaults, 'ContentEditorState');

const ConstantEditorStateActive: Record.Factory<ContentEditorActiveShape> =
  Record(
    {
      ...contentEditorDefaults,
      subtree: Expressions.getElement(wfdl`<Basic::Paragraph "p1"> {
      children: [
        <Basic::String "t1"> "Hello World" </Basic::String>,
      ],
    } </Basic::Paragraph>`)!,
      selection: RangeSelectionRecord({
        anchorNodeId: 't1',
        focusNodeId: 't1',
        anchorOffset: 0,
        focusOffset: 0,
        leftToRight: true,
      }) as SelectionRecordType,
    } as ContentEditorActiveShape,
    'ContentEditorState'
  );

export const activateContentEditor = (
  state: ContentEditorActive | ContentEditorInactive,
  props: {
    subtree: ExpressionElement;
    selection: SelectionRecordType;
  }
): ContentEditorActive => {
  return ConstantEditorStateActive({
    subtree: props.subtree,
    selection: props.selection,
    undoRedoCache: state.undoRedoCache || contentEditorDefaults.undoRedoCache,
    isOnPrimaryLocale: state.isOnPrimaryLocale,
    isPageBranch: state.isPageBranch,
    isAutoSaveDisabled: state.isAutoSaveDisabled,
  });
};

export const deactivateContentEditor = (
  state: ContentEditorActive
): ContentEditorInactive => {
  return ContentEditorState({
    undoRedoCache: state.undoRedoCache,
    isOnPrimaryLocale: state.isOnPrimaryLocale,
    isPageBranch: state.isPageBranch,
  });
};

export const activeContentEditor = (props: {
  subtree: ExpressionElement;
  selection: SelectionRecordType;
}) => activateContentEditor(ContentEditorState(), props);

export const getContentEditorActive = (
  state: ContentEditorActive | ContentEditorInactive
): ContentEditorActive | null =>
  state.selection && state.subtree ? state : null;

/**
 * True when the active Content Editor is the Rich Text property override
 * modal (activated with `fromModal`).
 */
export const isOverrideModalActive = (
  state: ContentEditorActive | ContentEditorInactive
): boolean => Boolean(getContentEditorActive(state)?.fromModal);
